import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit2, Trash2, CheckCircle, XCircle, Plus, DollarSign, Calendar } from 'lucide-react';
import { crmDealsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfirm } from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import Modal from '../../components/Modal';
import DealForm from '../../components/crm/DealForm';
import CrmActivityItem from '../../components/crm/CrmActivityItem';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import type { CrmDealStatus } from '../../types';

// Domain status taxonomy → token-colored text label.
const STATUS_COLOR: Record<CrmDealStatus, string> = {
  open:    'var(--scpa-primary)',
  won:     'var(--scpa-status-ok)',
  lost:    'var(--scpa-status-error)',
  on_hold: 'var(--scpa-status-warning)',
};

export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [editing, setEditing] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [showLostModal, setShowLostModal] = useState(false);

  const { data: deal, isLoading } = useQuery({
    queryKey: ['crm-deal', id, cid],
    queryFn: () => crmDealsApi.get(id!),
    enabled: !!id && !!cid,
  });

  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) => crmDealsApi.setStatus(id!, status, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm-deal', id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => crmDealsApi.delete(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['crm-deal-board'] }); navigate('/crm/deals/board'); },
  });

  const handleDelete = async () => {
    if (await confirm({ title: 'Delete deal', message: 'Delete this deal? This cannot be undone.', confirmLabel: 'Delete', danger: true })) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) return <div className="text-muted text-sm">Loading…</div>;
  if (!deal) return <div className="text-muted text-sm">Deal not found.</div>;

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={
          <span className="flex flex-col gap-1">
            <span>{deal.title}</span>
            <span className="flex items-center gap-3 text-[13px] font-normal">
              <span style={{ fontWeight: 700, textTransform: 'capitalize', color: STATUS_COLOR[deal.status] }}>{deal.status.replace('_', ' ')}</span>
              {deal.stage && <span className="flex items-center gap-1 text-muted"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: deal.stage.color }} />{deal.stage.name}</span>}
            </span>
          </span>
        }
        actions={
          <>
            {deal.status === 'open' && (
              <>
                <button onClick={() => statusMutation.mutate({ status: 'won' })} className="btn btn-primary flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Won
                </button>
                <button onClick={() => setShowLostModal(true)} className="btn btn-danger flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Lost
                </button>
              </>
            )}
            <button onClick={() => setEditing(true)} className="btn btn-secondary flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
            <button onClick={handleDelete} className="btn btn-danger"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details sidebar */}
        <div className="space-y-4">
          <div className="card space-y-3">
            <h3 className="section-title">Deal Info</h3>
            {deal.value && (
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-muted" />
                <span className="font-semibold text-ok">{deal.value.toLocaleString()} {deal.currency}</span>
              </div>
            )}
            {deal.probability != null && (
              <div className="text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-muted">Win probability</span>
                  <span className="font-medium text-fg">{deal.probability}%</span>
                </div>
                <div className="h-1.5 bg-row-odd rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${deal.probability}%` }} />
                </div>
              </div>
            )}
            {deal.expectedCloseDate && (
              <div className="flex items-center gap-2 text-sm text-fg">
                <Calendar className="w-4 h-4 text-muted" />
                <span>Close {new Date(deal.expectedCloseDate).toLocaleDateString()}</span>
              </div>
            )}
            {deal.contact && (
              <Link to={`/crm/contacts/${deal.contact.id}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                Contact: {deal.contact.firstName} {deal.contact.lastName}
              </Link>
            )}
            {deal.organization && (
              <Link to={`/crm/organizations/${deal.organization.id}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                {deal.organization.name}
              </Link>
            )}
            {deal.owner && (
              <p className="text-xs text-muted">Owner: {deal.owner.name}</p>
            )}
          </div>
          {deal.description && (
            <div className="card">
              <h3 className="section-title mb-2">Description</h3>
              <p className="text-sm text-muted whitespace-pre-wrap">{deal.description}</p>
            </div>
          )}
          {deal.lostReason && (
            <div className="card" style={{ borderColor: 'var(--scpa-status-error)' }}>
              <h3 className="section-title mb-1 text-err">Lost Reason</h3>
              <p className="text-sm text-muted">{deal.lostReason}</p>
            </div>
          )}
        </div>

        {/* Activities */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="section-title">Activities</h2>
            <button onClick={() => setShowActivityForm(true)} className="btn btn-primary flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Log Activity
            </button>
          </div>
          {!deal.activities?.length ? (
            <EmptyState>No activities yet. Use <b>Log Activity</b> to record one.</EmptyState>
          ) : (
            deal.activities.map(a => (
              <CrmActivityItem key={a.id} activity={a} onUpdated={() => queryClient.invalidateQueries({ queryKey: ['crm-deal', id] })} />
            ))
          )}
        </div>
      </div>

      {/* Lost reason modal */}
      <Modal
        open={showLostModal}
        onClose={() => setShowLostModal(false)}
        title="Mark as Lost"
        width={384}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowLostModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => { statusMutation.mutate({ status: 'lost', reason: lostReason }); setShowLostModal(false); }}>Mark Lost</button>
          </>
        }
      >
        <div>
          <label className="label">Reason (optional)</label>
          <input className="input w-full" value={lostReason} onChange={e => setLostReason(e.target.value)} placeholder="Why was this deal lost?" />
        </div>
      </Modal>

      {editing && <DealForm deal={deal} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); queryClient.invalidateQueries({ queryKey: ['crm-deal', id] }); }} />}
      {showActivityForm && <CrmActivityForm defaults={{ dealId: id }} onClose={() => setShowActivityForm(false)} onSaved={() => { setShowActivityForm(false); queryClient.invalidateQueries({ queryKey: ['crm-deal', id] }); }} />}
    </div>
  );
}
